import { useQueryClient } from "@tanstack/react-query";
import { Activity, HelpCircle, MessageSquare, Settings, Wrench } from "lucide-react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { AgenteConfig } from "@/components/admin/agenti/AgenteConfig";
import { AgenteConversazioni } from "@/components/admin/agenti/AgenteConversazioni";
import { AgenteDomande } from "@/components/admin/agenti/AgenteDomande";
import { AgenteHero } from "@/components/admin/agenti/AgenteHero";
import { AgenteSalute } from "@/components/admin/agenti/AgenteSalute";
import { AgenteStrumenti } from "@/components/admin/agenti/AgenteStrumenti";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAgente } from "@/hooks/useAdminAgenti";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "salute", label: "Salute", Icon: Activity },
  { key: "conversazioni", label: "Conversazioni", Icon: MessageSquare },
  { key: "domande", label: "Domande", Icon: HelpCircle },
  { key: "strumenti", label: "Strumenti", Icon: Wrench },
  { key: "cfg", label: "Configurazione", Icon: Settings },
];

export function AdminAgentePage() {
  const { slug } = useParams<{ slug: string }>();
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const { data: agente, isLoading, isFetching } = useAdminAgente(slug);

  const tab = sp.get("tab") ?? "salute";
  const setTab = (k: string) =>
    setSp(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set("tab", k);
        return n;
      },
      { replace: true },
    );

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!agente) return <Navigate to="/admin/agenti" replace />;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <AgenteHero
        agente={agente}
        fetching={isFetching}
        onRefresh={() => qc.invalidateQueries({ queryKey: ["admin", "agente", slug] })}
      />

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              "flex items-center gap-2 border-b-2 px-4 py-2.5 text-[13.5px] font-semibold transition-colors",
              tab === t.key
                ? "border-brand-700 text-brand-700"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <t.Icon className="size-4" />
            {t.label}
          </button>
        ))}
      </div>

      {slug && tab === "salute" && <AgenteSalute slug={slug} />}
      {slug && tab === "conversazioni" && (
        <AgenteConversazioni slug={slug} canali={agente.canali} />
      )}
      {slug && tab === "domande" && <AgenteDomande slug={slug} />}
      {slug && tab === "strumenti" && <AgenteStrumenti slug={slug} />}
      {tab === "cfg" && <AgenteConfig agente={agente} />}
    </div>
  );
}
